import { auth } from '../firebase/config';
import { getApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/userUtils';

const BASE_PATH = '/api/class-rosters';

const request = async (path, { method = 'GET', body } = {}) => {
  const headers = await getAuthHeaders(auth.currentUser);
  const response = await fetch(getApiUrl(`${BASE_PATH}${path}`), {
    method,
    headers: { ...headers, 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(typeof data?.detail === 'string' ? data.detail : `HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return data;
};

export const listClassRosters = async () => (await request('')).rosters || [];

export const listAllClassRosters = async () => {
  const data = await request('?scope=all');
  return {
    rosters: data.rosters || [],
    currentSchoolYear: data.current_school_year,
    currentSemester: data.current_semester,
  };
};

export const createClassRoster = async ({ label, names, createdFrom = 'batch', semester, schoolYear }) => (
  (await request('', {
    method: 'POST',
    body: {
      label,
      names,
      created_from: createdFrom,
      ...(semester ? { semester } : {}),
      ...(schoolYear ? { school_year: schoolYear } : {}),
    },
  })).roster
);

export const updateClassRoster = async (rosterId, patch) => (
  (await request(`/${encodeURIComponent(rosterId)}`, { method: 'PATCH', body: patch })).roster
);

export const copyClassRoster = async (rosterId, { semester, schoolYear } = {}) => (
  (await request(`/${encodeURIComponent(rosterId)}/copy`, {
    method: 'POST',
    body: { ...(semester ? { semester } : {}), ...(schoolYear ? { school_year: schoolYear } : {}) },
  })).roster
);

export const deleteClassRoster = (rosterId) => request(`/${encodeURIComponent(rosterId)}`, { method: 'DELETE' });

/** 명단을 지우면 같이 지워지는 메모 수(남은 명단 어디에도 없는 학생의 메모). */
export const getRosterDeletePreview = async (rosterId) => (
  (await request(`/${encodeURIComponent(rosterId)}/delete-preview`)).memo_count || 0
);

export const learnRosterAlias = async (oldName, newName) => (
  (await request('/learn', { method: 'POST', body: { old_name: oldName, new_name: newName } })).learned || 0
);

export const matchClassRoster = (names, rosterId = null, clues = null) => request('/match', {
  method: 'POST',
  body: {
    names,
    ...(rosterId ? { roster_id: rosterId } : {}),
    ...(Array.isArray(clues) && clues.some(clue => clue.file_name || clue.head) ? { clues } : {}),
  },
});

/** 한글·워드·PDF 명렬표를 표의 행·칸으로 받는다. 서버는 AI 없이 읽기만 하고 저장하지 않는다. */
export const readClassRosterFileRows = async (file, { timeoutMs = 60000 } = {}) => {
  const { 'Content-Type': _json, ...headers } = await getAuthHeaders(auth.currentUser);
  const form = new FormData();
  form.append('file', file);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(getApiUrl(`${BASE_PATH}/file-rows`), {
      method: 'POST',
      headers,
      body: form,
      signal: controller.signal,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(typeof data?.detail === 'string' ? data.detail : `HTTP ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return data.rows || [];
  } finally {
    clearTimeout(timer);
  }
};

export const saveRosterAliases = async (rosterId, aliases) => (
  (await request(`/${encodeURIComponent(rosterId)}`, { method: 'PATCH', body: { aliases } })).roster
);

/** 명단 하나. 기록 모으기보다 훨씬 가벼워서 학생 화면을 먼저 그릴 때 쓴다. */
export const getClassRoster = async (rosterId) => (await request(`/${encodeURIComponent(rosterId)}`)).roster;

/** 명단 학년도의 채점·생기부 다중 생성·제출 기록을 학생별로 모은다. */
export const getRosterStudentRecords = async (rosterId) => {
  const data = await request(`/${encodeURIComponent(rosterId)}/records`);
  return { roster: data.roster, students: data.students || [], truncated: Boolean(data.truncated) };
};

/** 단일 생성 기록을 명단 학생에게 담는다. sid가 없으면 뺀다. */
export const linkSingleRecord = (rosterId, { recordId, sid = null, versionIndex = 0 }) => request(
  `/${encodeURIComponent(rosterId)}/link-single`,
  { method: 'POST', body: { record_id: recordId, sid, version_index: versionIndex } },
);

const studentPath = (rosterId, sid) => `/${encodeURIComponent(rosterId)}/students/${encodeURIComponent(sid)}`;

/**
 * 학생 메모·고정 정보(임원·진로희망). 같은 학년도라면 1학기 명단에서 쓴 메모도 복사한 2학기 명단에서 보인다.
 * 다른 학년도 메모는 otherYearMemos로 따로 온다(생기부 입력에 넣지 않는다).
 */
export const getStudentNotes = async (rosterId, sid) => {
  const data = await request(`${studentPath(rosterId, sid)}/notes`);
  return {
    profile: data.profile || { role: '', role_period: '', career: '' },
    memos: data.memos || [],
    otherYearMemos: data.other_year_memos || [],
  };
};

/** 같은 저장이 다시 가도(재시도) 한 번만 남도록 화면에서 메모 id를 만든다. */
export const newMemoClientId = () => {
  const random = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '')
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  return random.slice(0, 32);
};

/** date(YYYY-MM-DD)를 비우면 서버가 오늘로 둔다. */
export const addStudentMemo = async (rosterId, sid, { text, area = '', date = '', clientId = '' }) => (
  (await request(`${studentPath(rosterId, sid)}/notes`, {
    method: 'POST',
    body: { text, area, ...(date ? { date } : {}), ...(clientId ? { client_id: clientId } : {}) },
  })).memo
);

/** 여러 학생에게 한 번에 남긴다. source: 'group'(여러 명 고르기) | 'paste'(붙여넣기 나누기). 하나라도 틀리면 아무것도 남지 않는다. */
export const addStudentMemos = async (rosterId, memos, source = 'group') => (
  (await request(`/${encodeURIComponent(rosterId)}/notes/bulk`, {
    method: 'POST',
    body: {
      source,
      memos: memos.map(({ sid, text, area = '', date = '', clientId = '' }) => ({
        sid, text, area, ...(date ? { date } : {}), ...(clientId ? { client_id: clientId } : {}),
      })),
    },
  })).memos || []
);

export const updateStudentMemo = async (noteId, patch) => (
  (await request(`/notes/${encodeURIComponent(noteId)}`, { method: 'PATCH', body: patch })).memo
);

export const deleteStudentMemo = (noteId) => request(`/notes/${encodeURIComponent(noteId)}`, { method: 'DELETE' });

export const saveStudentProfile = async (rosterId, sid, profile) => (
  (await request(`${studentPath(rosterId, sid)}/profile`, { method: 'PUT', body: profile })).profile
);

/** 반 전체 생기부: 학생(sid)마다 명단 학년도 메모와 고정 정보. */
export const getRosterNotes = async (rosterId) => (await request(`/${encodeURIComponent(rosterId)}/notes`)).students || {};

export const getMemoCounts = async (rosterId) => (await request(`/${encodeURIComponent(rosterId)}/memo-counts`)).counts || {};
